import { keepPreviousData, useQuery } from '@tanstack/react-query';

import {
  type PickupPointsMapParams,
  type PickupPointsParams,
  PickupPointsRequests,
} from '~/api/PickupPoints';

const FIVE_MINUTES = 5 * 60 * 1000;

/**
 * Fetches pickup points (ПВЗ) from the wms-connector service.
 * Pass city/street or a map bounding box to filter; results are cached per params.
 * Keeps the previous viewport's points visible while the next one loads so the
 * markers don't flicker as the user pans/zooms the map.
 */
export const usePickupPoints = (params?: PickupPointsParams, enabled = true) =>
  useQuery({
    queryKey: ['pickupPoints', params],
    queryFn: () =>
      PickupPointsRequests.getPickupPoints(params).then(({ data }) => data),
    staleTime: FIVE_MINUTES,
    placeholderData: keepPreviousData,
    enabled,
  });

/**
 * Fetches the pickup points of a map viewport from the v2 endpoint: the points
 * it can show individually plus clusters for the dense areas.
 */
export const usePickupPointsMap = (
  params?: PickupPointsMapParams,
  enabled = true,
) =>
  useQuery({
    queryKey: ['pickupPointsMap', params],
    queryFn: () =>
      PickupPointsRequests.getPickupPointsMap(
        params as PickupPointsMapParams,
      ).then(({ data }) => data),
    staleTime: FIVE_MINUTES,
    placeholderData: keepPreviousData,
    enabled: enabled && !!params,
  });

/**
 * Fetches a single pickup point (ПВЗ) by its UUID, e.g. to resolve the
 * `pickup_point_uuid` of an order into a human-readable address.
 */
export const usePickupPoint = (id?: string | null) =>
  useQuery({
    queryKey: ['pickupPoint', id],
    queryFn: () => PickupPointsRequests.getPickupPointById(id as string),
    staleTime: FIVE_MINUTES,
    enabled: !!id,
  });

/** Street/city filter sets tried by one directory search; their pages are merged. */
export type PickupPointsSearchFilters = Pick<
  PickupPointsParams,
  'city' | 'street'
>[];

const SEARCH_PAGE_SIZE = 30;

/**
 * Directory-wide text search. The list endpoint matches `street` and `city`
 * only (substring, case-insensitive, ANDed when both are given), so the caller
 * passes every combination worth trying; the first page of each is fetched in
 * parallel and merged without duplicates. A failed combination is skipped.
 */
export const usePickupPointsSearch = (
  filters: PickupPointsSearchFilters,
  enabled = true,
) =>
  useQuery({
    queryKey: ['pickupPointsSearch', filters],
    queryFn: async () => {
      const pages = await Promise.allSettled(
        filters.map((filter) =>
          PickupPointsRequests.getPickupPoints({
            ...filter,
            page: { current: 1, size: SEARCH_PAGE_SIZE },
          }).then(({ data }) => data),
        ),
      );
      const seen = new Set<string>();
      return pages
        .flatMap((page) => (page.status === 'fulfilled' ? page.value : []))
        .filter((point) => {
          if (seen.has(point.id)) {
            return false;
          }
          seen.add(point.id);
          return true;
        });
    },
    staleTime: FIVE_MINUTES,
    placeholderData: keepPreviousData,
    enabled: enabled && filters.length > 0,
  });
